/**
 * Wizard constants for attack path modals.
 *
 * These constants ensure consistent height between loading skeletons and content
 * to prevent layout shifts during loading->content transitions.
 */

import {
  ROW_HEIGHT,
  TABLE_HEADER_HEIGHT,
} from '@/sections/attacks/attackPaths/components/wizards/components/SelectionTable';

/**
 * Number of rows the wizard tables show in edit mode (SelectionTable maxRows={6}).
 * The empty-state and loading-skeleton heights below are derived from this so they
 * stay in lockstep with the populated table and never produce a layout jump.
 */
const MAX_EDIT_ROWS = 6;

/**
 * Height constants for wizard step content areas.
 * Derived from SelectionTable: MAX_EDIT_ROWS (6) * ROW_HEIGHT (48px) + TABLE_HEADER_HEIGHT (32px) = 320px
 *
 * The header height must be included because it's inside the scroll container.
 * This value matches the "Add custom" fallback sections and SelectionTable
 * component heights, ensuring no height jump during loading transitions.
 *
 * The `*_CLASS` literals must stay hardcoded (`min-h-[320px]`) because Tailwind
 * statically extracts class names at build time and cannot see a computed value.
 * Keep the literal in sync with STEP_CONTENT_HEIGHT.
 */
export const STEP_CONTENT_HEIGHT =
  MAX_EDIT_ROWS * ROW_HEIGHT + TABLE_HEADER_HEIGHT; // 320px
export const STEP_CONTENT_HEIGHT_CLASS = 'min-h-[320px]' as const;

/**
 * Scroll indicator height: py-2 (16px padding) + content (~20px) = 36px
 */
export const SCROLL_INDICATOR_HEIGHT = 36;

/**
 * Total height when pagination is enabled (table + indicator space)
 */
export const STEP_CONTENT_WITH_PAGINATION_HEIGHT =
  STEP_CONTENT_HEIGHT + SCROLL_INDICATOR_HEIGHT; // 356px
export const STEP_CONTENT_WITH_PAGINATION_HEIGHT_CLASS =
  'min-h-[356px]' as const;

/**
 * Unified wizard step height for consistent modal sizing across all steps.
 * Set to 304px to match IconGrid (3 icon rows × 96px + 16px padding).
 * Using a single height prevents modal resizing between wizard steps.
 */
export const WIZARD_STEP_HEIGHT = 304;
export const WIZARD_STEP_HEIGHT_CLASS = 'min-h-[304px]' as const;

/**
 * Unified minimum height for all wizard step containers.
 * Ensures consistent modal height across all steps (search, icon, status, etc.).
 *
 * Derived from search steps: description + search input + table (6 rows) +
 * scroll indicator + CurrentlyEditingDisplay (edit mode) + gaps = 492px
 */
export const WIZARD_STEP_MIN_HEIGHT = 492;
export const WIZARD_STEP_MIN_HEIGHT_CLASS = 'min-h-[492px]' as const;
